import React, { useState, useCallback, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Check, X } from 'lucide-react';

/**
 * Modal cover-image cropper. Wraps react-easy-crop and returns a cropped
 * JPEG Blob via onConfirm. Aspect ratio defaults to 16:9 (matches the
 * landing/dashboard banner shape).
 */
export default function CoverCropper({ imageSrc, onConfirm, onCancel, aspect = 16 / 9 }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [working, setWorking] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const previewTimer = useRef(null);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setPixels(croppedPixels);
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const blob = await renderCrop(imageSrc, croppedPixels);
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Preview render failed', e);
      }
    }, 150);
  }, [imageSrc]);

  useEffect(() => () => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const handleConfirm = async () => {
    if (!pixels) return;
    setWorking(true);
    try {
      const blob = await renderCrop(imageSrc, pixels);
      onConfirm(blob);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Crop failed', e);
      alert('Crop failed — please try a different image.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-midnight/90 backdrop-blur-sm p-4">
      <div className="bg-midnight border border-champagne/20 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <h3 className="text-text-light font-medium">Adjust cover</h3>
          <button
            type="button"
            onClick={onCancel}
            className="p-2.5 rounded-lg hover:bg-white/10 text-text-muted min-w-[44px] min-h-[44px] flex items-center justify-center"
            title="Cancel"
            aria-label="Cancel cover crop"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pt-3 text-xs text-text-muted">
          Drag the image and zoom so the area you want sits inside the highlighted box. Only what's inside the box becomes your cover.
        </div>
        <div className="relative w-full h-72 sm:h-96 bg-black">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid={true}
            objectFit="contain"
            style={{
              cropAreaStyle: {
                border: '2px solid #D4AF37',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
                color: 'rgba(0,0,0,0.55)',
              },
            }}
          />
        </div>
        {previewUrl && (
          <div className="px-5 pt-4">
            <div className="text-xs text-text-muted mb-1">Preview (what will be saved)</div>
            <img
              src={previewUrl}
              alt="Cover preview"
              className="w-full rounded-lg border border-champagne/30"
              style={{ aspectRatio: `${aspect}` }}
            />
          </div>
        )}

        <div className="px-5 py-4 space-y-3 border-t border-white/10">
          <label className="flex items-center gap-3 text-sm text-text-muted">
            Zoom
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 accent-champagne"
              aria-label="Zoom"
            />
          </label>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-xl border border-white/10 text-text-muted text-sm hover:bg-white/5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!pixels || working}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-champagne text-midnight font-medium text-sm hover:bg-champagne-light disabled:opacity-50"
              data-testid="cover-crop-confirm"
            >
              <Check className="w-4 h-4" />
              {working ? 'Cropping…' : 'Use this crop'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Render a canvas-cropped JPEG and return as a Blob.
function renderCrop(imageSrc, pixels) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixels.width;
      canvas.height = pixels.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        img,
        pixels.x, pixels.y, pixels.width, pixels.height,
        0, 0, pixels.width, pixels.height
      );
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Canvas empty'))),
        'image/jpeg',
        0.9
      );
    };
    img.onerror = reject;
    img.src = imageSrc;
  });
}
